import { z } from 'zod';
import { operationPlanSchema } from './operation-plan.js';

/**
 * Map settings singleton — admin-controlled flags that affect map UX.
 * Currently exposes the list of disabled hexagon region keys
 * (matching ATLAS tile.key entries on the web side).
 */

export const regionKeySchema = z.string().trim().min(1).max(64);

export const mapSettingsSchema = z.object({
  disabledRegionKeys: z.array(regionKeySchema),
  updatedAt: z.string().datetime(),
  updatedBy: z.string().nullable(),
});
export type MapSettingsDTO = z.infer<typeof mapSettingsSchema>;

export const patchMapSettingsSchema = z
  .object({
    disabledRegionKeys: z.array(regionKeySchema).optional(),
  })
  .refine((p) => Object.keys(p).length > 0, {
    message: 'Patch must include at least one field',
  });
export type PatchMapSettingsInput = z.infer<typeof patchMapSettingsSchema>;

export const DEFAULT_MAP_SETTINGS: Omit<MapSettingsDTO, 'updatedAt' | 'updatedBy'> = {
  disabledRegionKeys: [],
};

/**
 * Admin-only "New war" reset payload. Wipes every map object and
 * replaces the disabled hex list with the provided keys (usually the
 * full ATLAS so the planner starts blank).
 */
export const newWarRequestSchema = z.object({
  disabledRegionKeys: z.array(regionKeySchema),
});
export type NewWarRequest = z.infer<typeof newWarRequestSchema>;

export const newWarResponseSchema = z.object({
  clearedObjects: z.number().int().nonnegative(),
  settings: mapSettingsSchema,
});
export type NewWarResponse = z.infer<typeof newWarResponseSchema>;

/**
 * Admin-only "New operation" reset payload. Wipes every map object and
 * arrow with mode="operation", then resets the operation plan to defaults.
 */
export const newOperationRequestSchema = z.object({});
export type NewOperationRequest = z.infer<typeof newOperationRequestSchema>;

export const newOperationResponseSchema = z.object({
  clearedObjects: z.number().int().nonnegative(),
  clearedArrows: z.number().int().nonnegative(),
  operationPlan: operationPlanSchema,
});
export type NewOperationResponse = z.infer<typeof newOperationResponseSchema>;
